import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, Button, IconButton, CircularProgress, Dialog, DialogContent } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import LockIcon from '@mui/icons-material/Lock';
import FlipCameraIosIcon from '@mui/icons-material/FlipCameraIos';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import ReplayIcon from '@mui/icons-material/Replay';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TimerIcon from '@mui/icons-material/Timer';
import TimerOffIcon from '@mui/icons-material/TimerOff';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';
import { uploadUserPhoto, compressImage } from '../services/storageService';
import { performVirtualTryOn } from '../services/segmind';
import { startTryOnInBackground, generateSessionId } from '../services/tryOnCache';

function PhotoCapture() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const productId = searchParams.get('p');
  const flow = searchParams.get('flow');

  const buildContactUrl = () => {
    const params = new URLSearchParams();
    if (productId) params.set('p', productId);
    if (flow) params.set('flow', flow);
    const qs = params.toString();
    return `/contact${qs ? `?${qs}` : ''}`;
  };

  const closeGuide = () => {
    setShowGuide(false);
  };
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const streamRef = useRef(null);
  const lightCanvasRef = useRef(null);
  const lightIntervalRef = useRef(null);

  const [showOnboarding, setShowOnboarding] = useState(false);
  // Photo guide popup — shown when user clicks "Scatta foto"
  const [showGuide, setShowGuide] = useState(false);
  const [mode, setMode] = useState(null); // null | 'camera' | 'gallery'
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [facingMode, setFacingMode] = useState('user');
  const [countdown, setCountdown] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [timerEnabled, setTimerEnabled] = useState(false);
  const [lowLight, setLowLight] = useState(false);
  const [lowLightDismissed, setLowLightDismissed] = useState(false);

  const product = JSON.parse(localStorage.getItem('currentProduct') || 'null');

  useEffect(() => {
    return () => {
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopLightMonitor = useCallback(() => {
    if (lightIntervalRef.current) {
      clearInterval(lightIntervalRef.current);
      lightIntervalRef.current = null;
    }
    setLowLight(false);
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    stopLightMonitor();
    setCameraActive(false);
  }, [stopLightMonitor]);

  // Sample the live video feed every 800ms on a tiny 16×16 canvas to estimate
  // scene luminance. If too low, show a banner — armocromia needs decent light
  // to read skin/hair/eye tones correctly.
  const startLightMonitor = useCallback(() => {
    stopLightMonitor();
    const SAMPLE_SIZE = 16;
    // Hysteresis thresholds on a 0–255 scale: turn ON below 60, OFF above 80
    const LOW_ENTER = 60;
    const LOW_EXIT = 80;

    if (!lightCanvasRef.current) {
      lightCanvasRef.current = document.createElement('canvas');
      lightCanvasRef.current.width = SAMPLE_SIZE;
      lightCanvasRef.current.height = SAMPLE_SIZE;
    }
    const sampleCanvas = lightCanvasRef.current;
    const sampleCtx = sampleCanvas.getContext('2d', { willReadFrequently: true });

    lightIntervalRef.current = setInterval(() => {
      const video = videoRef.current;
      if (!video || video.readyState < 2 || video.videoWidth === 0) return;
      try {
        sampleCtx.drawImage(video, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE);
        const { data } = sampleCtx.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE);
        let sum = 0;
        // data: [R,G,B,A, R,G,B,A, ...] — Rec. 601 luma
        for (let i = 0; i < data.length; i += 4) {
          sum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
        }
        const avg = sum / (data.length / 4);
        setLowLight(prev => {
          if (prev && avg > LOW_EXIT) return false;
          if (!prev && avg < LOW_ENTER) return true;
          return prev;
        });
      } catch {
        // getImageData may throw if canvas is tainted; skip this tick
      }
    }, 800);
  }, [stopLightMonitor]);

  const startCamera = async (facing) => {
    stopCamera();
    setLowLightDismissed(false);
    try {
      const constraints = {
        video: {
          facingMode: facing,
          // Chiediamo un'aspect ratio 3:4 (portrait) ideale
          // ma accettiamo qualsiasi cosa la camera offra
          aspectRatio: { ideal: 3 / 4 },
          width: { ideal: 720 },
          height: { ideal: 960 }
        }
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      // Detect actual facing from track settings (fallback can change requested facing)
      const track = stream.getVideoTracks()[0];
      const settings = track?.getSettings() || {};
      const actualFacing = settings.facingMode || facing;
      setFacingMode(actualFacing);
      setCameraActive(true);
      startLightMonitor();
    } catch (error) {
      console.error('Camera access error:', error);
      alert('Impossibile accedere alla fotocamera. Verifica i permessi.');
      setMode(null);
    }
  };

  const handleFlipCamera = () => {
    const newFacing = facingMode === 'user' ? 'environment' : 'user';
    startCamera(newFacing);
  };

  const handleStartCamera = () => {
    // Show the photo guide first; on close, the camera will open
    setShowGuide(true);
  };

  const closeGuideAndStartCamera = () => {
    setShowGuide(false);
    setMode('camera');
    setCapturedPhoto(null);
    startCamera('user');
  };

  const handleGallery = () => {
    fileInputRef.current?.click();
  };

  const toggleTimer = () => {
    setTimerEnabled(prev => !prev);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const raw = ev.target.result;
      let out = raw;
      try { out = await compressImage(raw, 1024, 0.75); } catch (err) { /* use raw */ }
      setCapturedPhoto(out);
      setMode('gallery');
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  };

  const capturePhoto = () => {
    if (timerEnabled) {
      setCountdown(5);
      const interval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            doCapture();
            return null;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      doCapture();
    }
  };

  const doCapture = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;

    // Cap resolution — 512px is enough for Segmind try-on + armocromia
    const maxDim = 512;
    let w = video.videoWidth;
    let h = video.videoHeight;
    if (w > maxDim || h > maxDim) {
      const scale = maxDim / Math.max(w, h);
      w = Math.round(w * scale);
      h = Math.round(h * scale);
    }
    canvas.width = w;
    canvas.height = h;

    const ctx = canvas.getContext('2d');

    // Mirror solo se camera frontale: preview e capture coincidono
    if (facingMode === 'user') {
      ctx.translate(w, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, 0, 0, w, h);

    const photo = canvas.toDataURL('image/jpeg', 0.75);
    setCapturedPhoto(photo);
    stopCamera();
    setCountdown(null);
  };

  const handleRetry = () => {
    setCapturedPhoto(null);
    if (mode === 'gallery') {
      setMode(null);
    } else {
      startCamera(facingMode);
    }
  };

  const handleConfirm = async () => {
    if (!capturedPhoto) return;

    setUploading(true);
    try {
      // Photo is already compressed at capture (512px, 0.75 quality) — no re-compression needed
      const photoToSave = capturedPhoto;

      // Save to localStorage
      try {
        localStorage.setItem('userPhoto', photoToSave);
        localStorage.setItem('photoTimestamp', Date.now().toString());
      } catch (storageErr) {
        console.error('localStorage quota exceeded, clearing old data:', storageErr);
        localStorage.removeItem('emailLog');
        localStorage.removeItem('smsLog');
        try {
          localStorage.setItem('userPhoto', photoToSave);
        } catch (retryErr) {
          console.error('localStorage save failed entirely:', retryErr);
        }
      }

      // Start Firebase upload in background — don't wait for it
      // The upload continues during the contact capture step
      uploadUserPhoto(capturedPhoto).catch(uploadErr => {
        console.error('Firebase upload failed, will use base64 fallback:', uploadErr);
      });

      // Pre-start try-on in background (skip for armocromia-only flow)
      // This lets Segmind process while the user enters their contact info.
      if (product && flow !== 'armocromia') {
        try {
          const sessionId = generateSessionId();
          localStorage.setItem('tryOnSessionId', sessionId);
          // performVirtualTryOn accepts (photo, product, email, sessionId).
          // We pass no email here (will be set later in ContactCapture flow).
          const tryOnPromise = performVirtualTryOn(capturedPhoto, product, null, sessionId);
          startTryOnInBackground(sessionId, tryOnPromise);
          console.log('🚀 Pre-started try-on with sessionId:', sessionId);
        } catch (preStartErr) {
          console.warn('Pre-start try-on failed (non-fatal):', preStartErr.message);
        }
      }

      // Navigate immediately to contact capture (no waiting for upload)
      navigate(buildContactUrl());
    } catch (error) {
      console.error('Error saving photo:', error);
      navigate(buildContactUrl());
    } finally {
      setUploading(false);
    }
  };

  // Onboarding privacy screen
  if (showOnboarding) {
    const steps = [
      'Scatta una foto del tuo viso o corpo intero',
      'Il nostro sistema sovrappone il capo alla tua foto',
      'Vedi subito come ti sta!'
    ];

    return (
      <Box
        sx={{
          height: '100dvh',
          background: 'linear-gradient(160deg, #1a1a2e 0%, #16213e 40%, #0f3460 100%)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            padding: '16px 24px',
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.3)' }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            sx={{
              ml: 2,
              fontSize: '18px',
              fontWeight: 700,
              color: 'white'
            }}
          >
            Prova virtuale
          </Typography>
        </Box>

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            px: 3,
            gap: { xs: 1, sm: 2 },
            py: 2,
            overflow: 'auto'
          }}
        >
          {/* Camera icon */}
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 1
            }}
          >
            <CameraAltIcon sx={{ fontSize: 40, color: 'white' }} />
          </Box>

          {/* Title */}
          <Typography
            sx={{
              fontSize: { xs: '22px', sm: '26px' },
              fontWeight: 800,
              color: 'white',
              textAlign: 'center',
              lineHeight: 1.3
            }}
          >
            Scatta un selfie per la prova virtuale
          </Typography>

          {/* Steps */}
          <Box sx={{ maxWidth: '340px', width: '100%', mt: 1 }}>
            {steps.map((text, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  mb: 2
                }}
              >
                <Box
                  sx={{
                    minWidth: 32,
                    height: 32,
                    borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.25)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '14px' }}>
                    {i + 1}
                  </Typography>
                </Box>
                <Typography sx={{ color: 'white', fontSize: '15px', lineHeight: 1.4 }}>
                  {text}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Privacy box */}
          <Box
            sx={{
              maxWidth: '340px',
              width: '100%',
              background: 'rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(12px)',
              borderRadius: '16px',
              padding: '16px 20px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              mt: 1
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <LockIcon sx={{ fontSize: 20, color: 'white' }} />
              <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '15px' }}>
                Le tue foto non vengono conservate
              </Typography>
            </Box>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '13px', lineHeight: 1.6, mb: 0.5 }}>
              Vengono eliminate subito dopo l'elaborazione, a meno che tu non scelga di salvare il risultato.
            </Typography>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '13px', lineHeight: 1.6 }}>
              Adottiamo i massimi standard di sicurezza per tutelare la tua privacy.
            </Typography>
          </Box>

          {/* CTA */}
          <Button
            fullWidth
            variant="contained"
            onClick={() => setShowOnboarding(false)}
            sx={{
              maxWidth: '340px',
              mt: 2,
              background: 'rgba(255, 255, 255, 0.95)',
              color: '#667eea',
              padding: '16px 24px',
              fontSize: '17px',
              fontWeight: 700,
              borderRadius: '16px',
              textTransform: 'none',
              boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
              '&:hover': {
                background: '#ffffff',
                transform: 'translateY(-2px)',
                transition: 'all 0.3s ease'
              }
            }}
          >
            Ho capito, scatta!
          </Button>
        </Box>
      </Box>
    );
  }

  // Selection screen
  if (!mode && !capturedPhoto) {
    return (
      <Box
        sx={{
          height: '100dvh',
          background: 'linear-gradient(160deg, #1a1a2e 0%, #16213e 40%, #0f3460 100%)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            padding: '16px 24px',
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.3)' }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            sx={{
              ml: 2,
              fontSize: '18px',
              fontWeight: 700,
              color: 'white',
              flex: 1
            }}
          >
            Scatta la tua foto
          </Typography>
          <IconButton
            onClick={() => setShowGuide(true)}
            sx={{
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.25)' }
            }}
          >
            <HelpOutlineIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            px: 3,
            py: 3,
            gap: { xs: 2, sm: 3 },
            overflow: 'auto'
          }}
        >
          {/* Product pill badge */}
          {product && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.2,
                background: 'rgba(255, 255, 255, 0.08)',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                backdropFilter: 'blur(10px)',
                borderRadius: '999px',
                px: 2,
                py: 0.9,
                maxWidth: '340px'
              }}
            >
              {(product.imageUrl || product.immagine_url) && (
                <Box
                  component="img"
                  src={product.imageUrl || product.immagine_url}
                  alt=""
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    flexShrink: 0,
                    border: '1px solid rgba(255,255,255,0.2)'
                  }}
                />
              )}
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: '10px',
                    fontWeight: 700,
                    color: 'rgba(255,255,255,0.5)',
                    textTransform: 'uppercase',
                    letterSpacing: '1.2px',
                    lineHeight: 1,
                    mb: 0.6
                  }}
                >
                  Stai provando
                </Typography>
                <Typography
                  sx={{
                    fontSize: '13px',
                    color: 'white',
                    fontWeight: 600,
                    lineHeight: 1.3,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '260px'
                  }}
                >
                  {product.name}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Title */}
          <Typography
            sx={{
              fontSize: { xs: '28px', sm: '34px' },
              fontWeight: 800,
              color: 'white',
              textAlign: 'center',
              lineHeight: 1.15,
              letterSpacing: '-0.5px',
              mt: 1
            }}
          >
            Come vuoi scattare
            <br />
            <Box
              component="span"
              sx={{
                background: 'linear-gradient(135deg, #9ba8ff, #c8a7ff)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              la tua foto?
            </Box>
          </Typography>

          <Typography
            sx={{
              fontSize: '14px',
              color: 'rgba(255, 255, 255, 0.55)',
              textAlign: 'center',
              maxWidth: '300px',
              mb: 1,
              lineHeight: 1.5
            }}
          >
            Bastano 5 secondi
          </Typography>

          {/* CTA buttons wrapper — perfect vertical alignment */}
          <Box
            sx={{
              width: '100%',
              maxWidth: '340px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              gap: 1.5,
              '& > .MuiButton-root': { marginLeft: '0 !important' }
            }}
          >
            {/* Option: Scatta foto */}
            <Button
              fullWidth
              variant="contained"
              startIcon={<CameraAltIcon sx={{ fontSize: '28px !important' }} />}
              onClick={handleStartCamera}
              sx={{
                background: 'rgba(255, 255, 255, 0.95)',
                color: '#667eea',
                py: { xs: 1.75, sm: 2 },
                fontSize: '16px',
                fontWeight: 700,
                borderRadius: '16px',
                textTransform: 'none',
                boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                '&:hover': {
                  background: '#ffffff',
                  transform: 'translateY(-2px)',
                  transition: 'all 0.3s ease'
                }
              }}
            >
              Scatta foto
            </Button>

            {/* Option: Gallery */}
            <Button
              fullWidth
              variant="outlined"
              startIcon={<PhotoLibraryIcon sx={{ fontSize: '24px !important' }} />}
              onClick={handleGallery}
              sx={{
                background: 'rgba(255, 255, 255, 0.05)',
                color: 'rgba(255,255,255,0.85)',
                py: { xs: 1.5, sm: 1.75 },
                fontSize: '15px',
                fontWeight: 600,
                borderRadius: '16px',
                textTransform: 'none',
                border: '1.5px solid rgba(255, 255, 255, 0.2)',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.12)',
                  border: '1.5px solid rgba(255, 255, 255, 0.4)',
                  transform: 'translateY(-1px)',
                  transition: 'all 0.3s ease'
                }
              }}
            >
              Carica dalla galleria
            </Button>

            <Typography
              sx={{
                fontSize: '12px',
                color: 'rgba(255,255,255,0.45)',
                textAlign: 'center',
                mt: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.75
              }}
            >
              🔒 La tua foto resta privata e non viene condivisa
            </Typography>
          </Box>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
        </Box>

        {/* Photo guide dialog — closing it starts the camera */}
        <PhotoGuideDialog open={showGuide} onClose={closeGuideAndStartCamera} />
      </Box>
    );
  }

  // Camera view or preview
  return (
    <Box
      sx={{
        height: '100dvh',
        background: 'linear-gradient(160deg, #1a1a2e 0%, #16213e 40%, #0f3460 100%)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* Hidden canvas for capture */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
      {/* Hidden file input for gallery access from camera view */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          padding: '8px 16px',
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          zIndex: 10
        }}
      >
        <IconButton
          onClick={() => {
            stopCamera();
            if (capturedPhoto) {
              handleRetry();
            } else {
              setMode(null);
            }
          }}
          sx={{
            color: 'white',
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.3)' }
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography sx={{ ml: 2, color: 'white', fontWeight: 700, fontSize: '18px', flex: 1 }}>
          {capturedPhoto ? 'Anteprima foto' : 'Scatta foto'}
        </Typography>
        {/* Timer + Flip camera in header */}
        {!capturedPhoto && cameraActive && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              onClick={toggleTimer}
              sx={{
                color: 'white',
                backgroundColor: timerEnabled
                  ? 'linear-gradient(135deg, #667eea, #764ba2)'
                  : 'rgba(255, 255, 255, 0.2)',
                background: timerEnabled
                  ? 'linear-gradient(135deg, #667eea, #764ba2)'
                  : 'rgba(255, 255, 255, 0.2)',
                '&:hover': { opacity: 0.9 }
              }}
            >
              {timerEnabled ? <TimerIcon /> : <TimerOffIcon />}
            </IconButton>
            <IconButton
              onClick={handleFlipCamera}
              sx={{
                color: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.3)' }
              }}
            >
              <FlipCameraIosIcon />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* Camera / Preview */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          py: 1,
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            maxWidth: '480px',
            maxHeight: '100%',
            ...(capturedPhoto
              ? { display: 'flex' }
              : { width: '100%', height: '100%' }),
            borderRadius: '28px',
            overflow: 'hidden',
            position: 'relative',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.35)',
            border: '3px solid rgba(255, 255, 255, 0.25)'
          }}
        >
          {capturedPhoto ? (
            <img
              src={capturedPhoto}
              alt="Preview"
              style={{
                display: 'block',
                maxWidth: '100%',
                maxHeight: '100%',
                width: 'auto',
                height: 'auto'
              }}
            />
          ) : (
            <>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                  transform: facingMode === 'user' ? 'scaleX(-1)' : 'none'
                }}
              />
              {/* Low-light banner — shown while scene is too dark for accurate armocromia */}
              {lowLight && !lowLightDismissed && countdown === null && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 12,
                    left: 12,
                    right: 12,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.2,
                    padding: '10px 40px 10px 12px',
                    borderRadius: '14px',
                    background: 'rgba(22, 33, 62, 0.82)',
                    backdropFilter: 'blur(14px)',
                    WebkitBackdropFilter: 'blur(14px)',
                    border: '1px solid rgba(102, 126, 234, 0.35)',
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(102, 126, 234, 0.15) inset',
                    zIndex: 5,
                    animation: 'lowLightIn 0.25s ease-out',
                    '@keyframes lowLightIn': {
                      from: { opacity: 0, transform: 'translateY(-6px)' },
                      to: { opacity: 1, transform: 'translateY(0)' }
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      minWidth: 32,
                      borderRadius: '10px',
                      background: 'linear-gradient(135deg, #667eea, #764ba2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '17px',
                      boxShadow: '0 4px 12px rgba(102,126,234,0.45)'
                    }}
                  >
                    💡
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: '13px', fontWeight: 800, color: 'white', lineHeight: 1.2 }}>
                      Poca luce nella stanza
                    </Typography>
                    <Typography sx={{ fontSize: '11.5px', color: 'rgba(255,255,255,0.75)', mt: 0.3, lineHeight: 1.4 }}>
                      Spostati verso una finestra o aggiungi luce: l'armocromia è più precisa con buona illuminazione.
                    </Typography>
                  </Box>
                  <IconButton
                    onClick={() => setLowLightDismissed(true)}
                    size="small"
                    aria-label="Chiudi"
                    sx={{
                      position: 'absolute',
                      top: 6,
                      right: 6,
                      color: 'rgba(255,255,255,0.6)',
                      padding: '4px',
                      '&:hover': { color: 'white', background: 'rgba(255,255,255,0.08)' }
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              )}
              {/* Countdown overlay */}
              {countdown !== null && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(0, 0, 0, 0.4)'
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '80px',
                      fontWeight: 900,
                      color: 'white',
                      textShadow: '0 4px 20px rgba(0,0,0,0.5)'
                    }}
                  >
                    {countdown}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Bottom controls */}
      <Box
        sx={{
          padding: '8px 24px',
          paddingBottom: '16px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 3
        }}
      >
        {capturedPhoto ? (
          <>
            <Button
              variant="outlined"
              startIcon={<ReplayIcon />}
              onClick={handleRetry}
              sx={{
                color: 'white',
                fontSize: '15px',
                fontWeight: 600,
                textTransform: 'none',
                px: 3,
                py: 1.5,
                borderRadius: '14px',
                border: '2px solid rgba(255, 255, 255, 0.4)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  border: '2px solid white'
                }
              }}
            >
              Riprova
            </Button>

            <Button
              variant="contained"
              startIcon={uploading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <CheckCircleIcon />}
              onClick={handleConfirm}
              disabled={uploading}
              sx={{
                background: 'rgba(255, 255, 255, 0.95)',
                color: '#667eea',
                fontSize: '15px',
                fontWeight: 700,
                textTransform: 'none',
                px: 4,
                py: 1.5,
                borderRadius: '14px',
                boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)',
                '&:hover': {
                  background: '#ffffff',
                  transform: 'translateY(-2px)',
                  transition: 'all 0.3s ease'
                }
              }}
            >
              {uploading ? 'Continua...' : 'Continua'}
            </Button>
          </>
        ) : (
          cameraActive && countdown === null && (
            <>
              {/* Gallery shortcut */}
              <IconButton
                onClick={handleGallery}
                sx={{
                  color: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  width: 48,
                  height: 48,
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.25)' }
                }}
              >
                <PhotoLibraryIcon />
              </IconButton>

              {/* Shutter button */}
              <Box
                onClick={capturePhoto}
                sx={{
                  width: '72px',
                  height: '72px',
                  borderRadius: '50%',
                  border: '4px solid rgba(255, 255, 255, 0.8)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'transform 0.15s ease',
                  '&:hover': { transform: 'scale(1.08)' },
                  '&:active': { transform: 'scale(0.92)' }
                }}
              >
                <Box
                  sx={{
                    width: '58px',
                    height: '58px',
                    borderRadius: '50%',
                    background: 'white',
                    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.2)'
                  }}
                />
                {timerEnabled && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -4,
                      right: -4,
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #667eea, #764ba2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid white',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.3)'
                    }}
                  >
                    <Typography sx={{ fontSize: '11px', fontWeight: 800, color: 'white' }}>5s</Typography>
                  </Box>
                )}
              </Box>

              {/* Flip camera */}
              <IconButton
                onClick={handleFlipCamera}
                sx={{
                  color: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  width: 48,
                  height: 48,
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.25)' }
                }}
              >
                <FlipCameraIosIcon />
              </IconButton>
            </>
          )
        )}
      </Box>

      {/* Photo guide dialog — also available from the camera view */}
      <PhotoGuideDialog open={showGuide} onClose={closeGuide} />
    </Box>
  );
}

/**
 * Dialog with visual photo guide: 3 BAD examples on top, 3 GOOD examples on bottom.
 * Shown on first visit to PhotoCapture and re-openable via help icon.
 */
function PhotoGuideDialog({ open, onClose }) {
  const badExamples = [
    { image: '/guide/bad-closeup.jpg', label: 'Troppo vicino', effect: null },
    { image: '/guide/bad-far.jpg', label: 'Troppo lontano', effect: null },
    { image: '/guide/bad-tilted.jpg', label: 'Inclinato', effect: 'tilt' }
  ];
  const goodExamples = [
    { image: '/guide/good-bust.jpg', label: 'Busto centrato' },
    { image: '/guide/good-fullbody.jpg', label: 'Corpo intero' },
    { image: '/guide/good-bg.jpg', label: 'Sfondo neutro' }
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '24px',
          background: 'linear-gradient(160deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
          border: '1px solid rgba(255,255,255,0.1)',
          overflow: 'hidden',
          m: 2
        }
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ position: 'relative', p: { xs: 2.5, sm: 3 } }}>
          {/* Close button */}
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'rgba(255,255,255,0.6)',
              '&:hover': { color: 'white', backgroundColor: 'rgba(255,255,255,0.1)' },
              zIndex: 1
            }}
            size="small"
          >
            <CloseIcon />
          </IconButton>

          {/* Title */}
          <Typography
            sx={{
              fontSize: { xs: '20px', sm: '22px' },
              fontWeight: 800,
              color: 'white',
              textAlign: 'center',
              lineHeight: 1.2,
              mt: 1,
              mb: 2.5
            }}
          >
            Come scattare{' '}
            <Box
              component="span"
              sx={{
                background: 'linear-gradient(135deg, #9ba8ff, #c8a7ff)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              la foto
            </Box>
          </Typography>

          {/* BAD row */}
          <Typography
            sx={{
              fontSize: '11px',
              fontWeight: 700,
              color: '#ef4444',
              textTransform: 'uppercase',
              letterSpacing: '1.5px',
              mb: 1,
              textAlign: 'center'
            }}
          >
            × Evita
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mb: 2.5 }}>
            {badExamples.map((ex) => (
              <Box key={ex.label} sx={{ textAlign: 'center' }}>
                <Box
                  sx={{
                    position: 'relative',
                    aspectRatio: '5 / 7',
                    borderRadius: '10px',
                    overflow: 'hidden',
                    border: '2px solid #ef4444',
                    boxShadow: '0 4px 16px rgba(239, 68, 68, 0.25)',
                    mb: 0.5,
                    backgroundColor: '#1a1a2e'
                  }}
                >
                  <Box
                    component="img"
                    src={ex.image}
                    alt={ex.label}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                      filter: 'grayscale(25%)',
                      opacity: 0.85,
                      transform: ex.effect === 'zoomOut'
                        ? 'scale(0.45)'
                        : ex.effect === 'tilt'
                        ? 'rotate(-15deg) scale(1.1)'
                        : 'none'
                    }}
                  />
                  {/* Red X badge */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      backgroundColor: '#ef4444',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.3)'
                    }}
                  >
                    <svg width="10" height="10" viewBox="0 0 10 10">
                      <line x1="3" y1="3" x2="7" y2="7" stroke="#0f3460" strokeWidth="1.8" strokeLinecap="round" />
                      <line x1="7" y1="3" x2="3" y2="7" stroke="#0f3460" strokeWidth="1.8" strokeLinecap="round" />
                    </svg>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.55)', fontWeight: 500 }}>
                  {ex.label}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* GOOD row */}
          <Typography
            sx={{
              fontSize: '11px',
              fontWeight: 700,
              color: '#4ade80',
              textTransform: 'uppercase',
              letterSpacing: '1.5px',
              mb: 1,
              textAlign: 'center'
            }}
          >
            ✓ Perfetto
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mb: 2.5 }}>
            {goodExamples.map((ex) => (
              <Box key={ex.label} sx={{ textAlign: 'center' }}>
                <Box
                  sx={{
                    position: 'relative',
                    aspectRatio: '5 / 7',
                    borderRadius: '10px',
                    overflow: 'hidden',
                    border: '2px solid #4ade80',
                    boxShadow: '0 4px 16px rgba(74, 222, 128, 0.2)',
                    mb: 0.5
                  }}
                >
                  <Box
                    component="img"
                    src={ex.image}
                    alt={ex.label}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block'
                    }}
                  />
                  {/* Green check badge */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      backgroundColor: '#4ade80',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.3)'
                    }}
                  >
                    <svg width="10" height="10" viewBox="0 0 10 10">
                      <path d="M 2 5 L 4.5 7 L 8 3" stroke="#0f3460" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.8)', fontWeight: 600 }}>
                  {ex.label}
                </Typography>
              </Box>
            ))}
          </Box>

          <Button
            fullWidth
            variant="contained"
            onClick={onClose}
            sx={{
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              color: 'white',
              py: 1.5,
              fontSize: '15px',
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: '14px',
              boxShadow: '0 8px 25px rgba(102,126,234,0.4)',
              '&:hover': {
                boxShadow: '0 12px 35px rgba(102,126,234,0.55)',
                transform: 'translateY(-1px)',
                transition: 'all 0.3s ease'
              }
            }}
          >
            Ho capito
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default PhotoCapture;
